import type * as A from '@/api';
import { Auth } from '@/aws-config';
import { logger } from '@/utils/logger';
import { graphql } from '@/utils/graphql';
import User from './user';
import Workspace from './workspace';
import Source from './source';
import Integration from './integration';
import ConnectionLite from './connectionLite';
import { addBackendSuffix } from '@/utils/namingConvention';
import { assertIsDefined } from '@/utils/typeChecks';
import Organization from './organization';
import { ConnectionUsage } from '@/models/connection';

const ns = 'app.services.connection';

type ConnectionConstructorInput = A.GetConnectionOnlyNativeQuery['getConnection'];

type ConnectionCreateInput = {
  organizationId: string;
  workspaceId: string;
  name?: string;
  fivetranConnectionId?: string;
  integrationId?: string;
  credentialsId?: string;
  isDeleted?: boolean;
};

type ConnectionUpdateInput = { id: string } & Partial<ConnectionCreateInput>;

// this is only for native connections, not fivetran ones
// we may refactor in the future when we have the needs to manage both types
export default class Connection {
  readonly id: string;

  readonly name: string;

  readonly organizationId: string;

  readonly workspaceId: string;

  readonly owner: User;

  readonly integration: Integration;

  readonly credentialsId: string;

  readonly isPaused?: boolean;

  readonly createdAt: Date;

  readonly pausedAt?: Date;

  private constructor(input: ConnectionConstructorInput) {
    assertIsDefined(input.integration, 'BEEM231108093322');
    assertIsDefined(input.credentialsId, 'BEEM231108093323');
    this.id = input.id;
    this.name = input.name ?? '';
    this.organizationId = input.organizationId;
    this.workspaceId = input.workspaceId;
    this.owner = new User(input.owner);
    this.integration = new Integration(input.integration);
    this.credentialsId = input.credentialsId;
    this.isPaused = input.isPaused ?? false;
    this.createdAt = new Date(input.createdAt);
    this.pausedAt = input.pausedAt ? new Date(input.pausedAt) : undefined;
  }

  static async getConnectionById(id: string): Promise<Connection> {
    logger.info(id, { label: 'app.services.connection.getConnectionById.id' });
    const data = await graphql('getConnectionOnlyNative', { id });
    return new Connection(data);
  }

  // static async getNativeConnectionsByOrganization(organizationId: string): Promise<Connection[]> {
  //   logger.info(organizationId, {
  //     label: 'app.services.connection.getNativeConnectionsByOrganization.organizationId',
  //   });
  //   const data = await graphql('listConnectionsByOrganizationNative', { organizationId });
  //   return data.items.map((el) => new Connection(el));
  // }

  // static async getFivetranConnectionsByOrganization(organizationId: string) {
  //   logger.info(organizationId, {
  //     label: 'app.services.connection.getFivetranConnectionsByOrganization.organizationId',
  //   });
  //   const data = await graphql('listConnectionsByOrganizationFivetran', { organizationId });
  //   return data.items;
  // }

  static async getConnectionUsage(organization: Organization): Promise<ConnectionUsage> {
    const { connectionLimit, id: organizationId } = organization;

    const nativeConnections = await graphql('listConnectionsByOrganizationNative', {
      organizationId,
      filter: { isDeleted: { ne: true } },
    });

    const fivetranConnections = await graphql('listConnectionsByOrganizationFivetran', {
      organizationId,
      filter: { isDeleted: { ne: true } },
    });

    const current = nativeConnections.items.length + fivetranConnections.items.length;
    const remaining = Math.max(0, connectionLimit - current);
    const isAtLimit = current >= connectionLimit;

    const usage = {
      current,
      limit: connectionLimit,
      remaining,
      isAtLimit,
    };

    return usage;
  }

  static async checkConnectionLimit(organizationId: string): Promise<void> {
    const organization = await Organization.getOrganizationById(organizationId);
    const usage = await Connection.getConnectionUsage(organization);

    if (usage.isAtLimit) {
      throw new Error(
        `BEEM_CONNECTION_LIMIT_REACHED: Organization has reached the maximum number of connections (${usage.limit}). Current count: ${usage.current}`,
      );
    }
  }

  static async getNativeConnectionsByWorkspace(workspaceId: string): Promise<Connection[]> {
    logger.info(workspaceId, {
      label: 'app.services.connection.getNativeConnectionsByWorkspace.workspaceId',
    });
    const data = await graphql('listConnectionsByWorkspaceNative', {
      workspaceId,
      filter: { isDeleted: { ne: true } },
    });
    return data.items.map((el) => new Connection(el));
  }

  static async getFivetranConnectionsByWorkspace(workspaceId: string) {
    logger.info(workspaceId, {
      label: 'app.services.connection.getFivetranConnectionsByWorkspace.workspaceId',
    });
    const data = await graphql('listConnectionsByWorkspaceFivetran', {
      workspaceId,
      filter: { isDeleted: { ne: true } },
    });
    return data.items;
  }

  static async getConnectionsIdAndNameByOrganization(
    organizationId: string,
  ): Promise<ConnectionLite[]> {
    logger.info(organizationId, {
      label: 'app.services.connection.getConnectionsIdAndNameByOrganization.organizationId',
    });
    const data = await graphql('listConnectionsByOrganizationNativeOnlyIdAndName', {
      organizationId,
      filter: { isDeleted: { ne: true } },
    });
    return data.items.map((el) => {
      assertIsDefined(el.integration, 'BEEM231108093324');
      return new ConnectionLite({ id: el.id, integrationName: el.integration.name });
    });
  }

  static async create(input: ConnectionCreateInput): Promise<string> {
    logger.info({ label: 'app.services.connection.create.input', message: input });
    await Connection.checkConnectionLimit(input.organizationId);
    const ownerId = (await Auth.currentAuthenticatedUser()).getUsername();
    const data = await graphql('createConnection', { input: { ...input, ownerId } });
    return data.id;
  }

  static async update(input: ConnectionUpdateInput): Promise<void> {
    logger.info({ label: 'app.services.connection.update.input', message: input });
    await graphql('updateConnection', { input });
  }

  static async updateFivetranConnectionState(id: string, isPaused: boolean): Promise<void> {
    logger.info({
      label: 'app.services.connection.updateFivetranConnectionState.input',
      message: { id, isPaused },
    });
    const { fivetranConnection } = await graphql('getConnectionOnlyFivetran', { id });
    assertIsDefined(fivetranConnection, 'BEEM2403221148');
    await graphql('updateConnection', {
      input: { id, isPaused, pausedAt: isPaused ? Math.round(Date.now() / 1000) : undefined },
    });
    await graphql('fivetranUpdateConnector', {
      query: { connectorId: fivetranConnection.id, paused: isPaused },
    });
  }

  static async softDeleteFivetranConnection(id: string): Promise<void> {
    const { fivetranConnection } = await graphql('getConnectionOnlyFivetran', { id });
    assertIsDefined(fivetranConnection, 'BEEM2403221148');

    const deletedConnector = await graphql('fivetranDeleteConnector', {
      query: { connectorId: fivetranConnection.id },
    });
    if (deletedConnector.code !== 'Success')
      throw new Error(
        `Could not delete Fivetran connection. Error message: ${deletedConnector.message}`,
      );
    await graphql('beemAgentFivetranUndeployConnector', {
      query: { glueDatabaseName: fivetranConnection.glueDatabaseName },
    });

    await graphql('updateFivetranConnection', {
      input: { id: fivetranConnection.id, isDeleted: true },
    });

    await graphql('updateConnection', { input: { id, isDeleted: true } });
  }

  // Native OAuth-style connections (e.g. Facebook via the Custom chooser)
  // have no FivetranConnection and no Source to tear down. The OAuth tokens
  // live in the customer's SSM (per /beem/oauth/<connectionId>); ask the agent
  // to remove them before flipping isDeleted on the Connection row. SSM cleanup
  // is best-effort: a failure here is logged but does not block the soft-delete.
  static async softDeleteNativeConnection(id: string): Promise<void> {
    logger.info({ label: `${ns}.softDeleteNativeConnection.id`, message: id });
    const { organizationId } = await graphql('getConnectionOnlyNative', { id });
    try {
      await graphql('beemAgentDeleteOAuthCredentials', {
        query: { orgId: organizationId, connectionId: id },
      });
    } catch (e) {
      logger.error({
        label: `${ns}.softDeleteNativeConnection.agentDelete`,
        message: `failed to delete SSM credentials for ${id}: ${(e as Error).message}`,
      });
    }
    await graphql('updateConnection', { input: { id, isDeleted: true } });
  }

  static async getKind(id: string): Promise<'native' | 'fivetran'> {
    // Reuses the existing getConnectionOnlyFivetran selection; native
    // connections come back with fivetranConnection: null.
    const data = await graphql('getConnectionOnlyFivetran', { id });
    return data.fivetranConnection ? 'fivetran' : 'native';
  }

  static async deployFivetranSourceIngestions(fivetranConnectionId: string): Promise<void> {
    logger.info({
      label: `${ns}.deployFivetranSourceIngestions.fivetranConnectionId`,
      message: fivetranConnectionId,
    });
    const fivetranConnection = await graphql('getFivetranConnection', { id: fivetranConnectionId });
    // Fivetran sends every Connect Card back here with `?id=`, including a re-authorization of a
    // source that was deployed long ago, and a reload repeats the visit. Deploying is done once.
    if (fivetranConnection.isDeployed) return;
    const sourceId = await Source.getSourceIdByFivetranConnectionId(fivetranConnectionId);
    const source = await Source.getSourceById(sourceId);
    const workspace = await Workspace.getWorkspaceById(source.workspaceId);
    const workspaceSeq = addBackendSuffix(String(workspace.seq));
    await graphql('beemAgentFivetranDeployConnector', {
      query: {
        fivetranServiceName: fivetranConnection.serviceName,
        fivetranConnectionId,
        fivetranConnectionName: fivetranConnection.name,
        glueDatabaseName: fivetranConnection.glueDatabaseName,
        workspaceSeq,
        dynamoDbSourceId: source.id,
        dynamoDbWorkspaceId: workspace.id,
      },
    });
    await graphql('updateFivetranConnection', {
      input: { id: fivetranConnectionId, isDeployed: true },
    });
  }
}
